import { computed, DestroyRef, effect, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Location } from '@angular/common';

import {
  GetMiniHomeV3Request,
  MiniHome,
  MiniHome_MiniHomeProducts,
  MiniHome_MiniHomeProducts_MiniHomeProduct,
} from '@proto/store/api/store/home';
import { BaseViewModel } from '../base-view-model';
import { ProductColorItem, ProductColorItem_Item } from '@proto/store/api/common/common';
import { MarketingCategoryModel } from '@common/marketing-category/marketing-category.model';
import { GridItem } from '@common/product-grid/model';
import { RouteHelper, RoutePaths } from '@helper/route-paths';
import { Constants } from '@helper/constants';
import { MiniRepositoryService } from '@service/mini-repository.service';
import { HomeModel } from '../home/home.model';
import { Urls } from '@helper/urls';
import { MiniHomeSort } from '@helper/mini-home';
import { MetaData } from '@helper/meta-data';
import { WindowDetectService } from '@service/window-detect.service';
import { HomeHeaderModel } from '@common/home-header/home-header.model';

import { isEqual } from 'lodash-es';
import { combineLatest, of } from 'rxjs';

@Injectable()
export class MiniHomeModel extends BaseViewModel<MiniHome> {
  readonly currentCategory = computed(
    () => this.apiResult().miniHomes.find(c => c.big?.nameEn === this.marketingCategoryModel.currentBigId()),
    { equal: (a, b) => isEqual(a, b) }
  );
  // 去除前幾筆資料後的小首頁商品資料
  readonly miniProducts = signal<MiniHome_MiniHomeProducts[]>([]);
  readonly baseCategoryGridMap = computed(
    () =>
      // 就是只有前幾筆資料
      this.apiResult().miniHomes.reduce((total, item) => {
        const sortMap = new Map<MiniHomeSort, GridItem[]>();
        Object.entries(MiniHomeSort).forEach((_, v) => {
          let gridItems: GridItem[] = [];
          switch (v as MiniHomeSort) {
            case MiniHomeSort.NORMAL: {
              gridItems = this.getCategoryGridMap(v, item.content);
              break;
            }
            case MiniHomeSort.SALES: {
              gridItems = this.getCategoryGridMap(v, item.sales);
              break;
            }
            case MiniHomeSort.NEW: {
              gridItems = this.getCategoryGridMap(v, item.newArrivals);
              break;
            }
            case MiniHomeSort.PRICE:
            case MiniHomeSort.PRICE_ASC:
            case MiniHomeSort.PRICE_DESC: {
              gridItems = [];
              break;
            }
          }
          sortMap.set(v, gridItems);
        });
        total.set(item.big?.nameEn ?? '', sortMap);
        return total;
      }, new Map<string, Map<MiniHomeSort, GridItem[]>>()),
    { equal: (a, b) => isEqual(a, b) }
  );
  readonly categoryGridMap = computed(
    () =>
      // 會把完整的資料跟一開始的幾筆之料合併
      this.apiResult().miniHomes.reduce((total, item) => {
        const categoryName = item.big?.nameEn ?? '';
        const miniProducts = this.miniProducts().find(x => x.big?.nameEn && x.big.nameEn === categoryName);
        const categoryGridMap = this.baseCategoryGridMap().get(categoryName);
        const sortMap = new Map<MiniHomeSort, GridItem[]>();
        Object.entries(MiniHomeSort).forEach((_, v) => {
          if (v !== MiniHomeSort.PRICE && v !== MiniHomeSort.PRICE_ASC && v !== MiniHomeSort.PRICE_DESC) {
            const baseGridItems = categoryName ? (categoryGridMap?.get(v) ?? []) : [];
            const baseSet = new Set(baseGridItems.map(item => item.meta?.productId ?? '').filter(id => !!id));
            const gridItems = this.getCategoryGridMap(v, miniProducts?.content ?? []).filter(
              item => !baseSet.has(item.meta?.productId ?? '')
            );
            sortMap.set(v, [...baseGridItems, ...gridItems]);
          } else {
            const target = this.miniProducts().find(x => x.big?.nameEn === categoryName);
            if (categoryName && target) {
              // 價格相關排序, 必須等到第二包資料回來, 才能顯示.
              sortMap.set(v, this.getCategoryGridMap(v, target.content));
            }
          }
        });
        total.set(categoryName, sortMap);
        return total;
      }, new Map<string, Map<MiniHomeSort, GridItem[]>>()),
    { equal: (a, b) => isEqual(a, b) }
  );
  readonly itemCount = signal(Constants.displayCount(this.detectService.isMobileDevice()));
  readonly gridItems = computed(
    () =>
      this.categoryGridMap()
        .get(this.currentCategory()?.big?.nameEn ?? '')
        ?.get(this.homeModel.sortBy())
        ?.slice(0, this.itemCount()) ?? [],
    { equal: (a, b) => isEqual(a, b) }
  );
  readonly fullProductCategories = new Set<string>();

  constructor(
    private lativRepository: MiniRepositoryService,
    private marketingCategoryModel: MarketingCategoryModel,
    private windowDetectService: WindowDetectService,
    private homeModel: HomeModel,
    private location: Location,
    headerViewModel: HomeHeaderModel
  ) {
    super(MiniHome.fromPartial({}));
    if (this.windowDetectService.isMobileDevice()) {
      effect(() => {
        const category = this.currentCategory()?.big?.nameEn ?? '';
        const isEvent = headerViewModel.isSalesEvent();
        if (category && !isEvent && (this.location.path().includes(RoutePaths.HOME) || !this.location.path().trim())) {
          this.location.replaceState(`${RouteHelper.miniHome(category)}${Urls.pathToParams(location.path())}`);
        }
      });
    }
    effect(() => this.getMiniHome(this.destroyRef));
  }

  getMiniHome(ref: DestroyRef): void {
    const category = this.marketingCategoryModel.currentBigId();
    if (category) {
      if (this.fullProductCategories.has(category)) {
        // 不用一直打 API.
        return;
      }
      this.fullProductCategories.add(category);
    }
    combineLatest([
      this.lativRepository.getMiniHomeV3(GetMiniHomeV3Request.fromPartial({})),
      category ? this.lativRepository.getMiniHomeV3(GetMiniHomeV3Request.fromPartial({ category })) : of(undefined),
    ])
      .pipe(takeUntilDestroyed(ref))
      .subscribe({
        next: res => {
          // 合併資料
          this.setApiResult(res[0]);
          this.setMiniHomeByCategory(
            res[1]?.miniHomes.find(m => m.big?.nameEn === category),
            category
          );
        },
        error: err => {
          this.setApiError(err);
          if (category) {
            this.fullProductCategories.delete(category);
          }
        },
      });
  }

  private setMiniHomeByCategory(categoryMiniHome: MiniHome_MiniHomeProducts | undefined, category: string): void {
    if (!categoryMiniHome || !category) {
      return;
    }
    this.miniProducts.update(items => {
      const products = items.map(x => MiniHome_MiniHomeProducts.fromPartial(x));
      products.push(categoryMiniHome);
      return products;
    });
  }

  private getCategoryGridMap(sort: MiniHomeSort, items: MiniHome_MiniHomeProducts_MiniHomeProduct[]): GridItem[] {
    let sorted: MiniHome_MiniHomeProducts_MiniHomeProduct[] = [];
    switch (sort) {
      case MiniHomeSort.NORMAL: {
        sorted = items.sort((a, b) => {
          if (a.hasInventory === b.hasInventory) {
            if (a.isFirstMediumCategory === b.isFirstMediumCategory) {
              return !!a.newArrivalPriority === !!b.newArrivalPriority ? 0 : b.newArrivalPriority ? 1 : -1;
            }
            return b.isFirstMediumCategory ? 1 : -1;
          }
          return b.hasInventory ? 1 : -1;
        });
        break;
      }
      case MiniHomeSort.NEW: {
        sorted = items
          .filter(item => item.newArrivalPriority > 0)
          .sort((a, b) => {
            if (a.newArrivalPriority == b.newArrivalPriority) {
              return this.secondSort(a, b);
            }
            return a.newArrivalPriority - b.newArrivalPriority;
          });
        break;
      }
      case MiniHomeSort.SALES: {
        sorted = items.sort((a, b) => {
          if (a.sales == b.sales) {
            return this.secondSort(a, b);
          }
          return b.sales - a.sales;
        });
        break;
      }
      case MiniHomeSort.PRICE_DESC:
      case MiniHomeSort.PRICE_ASC: {
        sorted = items.sort((a, b) => {
          if (!a.meta || !b.meta) {
            return 0;
          }
          const priceA = Number(a.meta.promotionPrice || a.meta.originPrice || '0');
          const priceB = Number(b.meta.promotionPrice || b.meta.originPrice || '0');
          if (priceA === priceB) {
            return this.secondSort(a, b);
          }
          return sort === MiniHomeSort.PRICE_ASC ? priceA - priceB : priceB - priceA;
        });
        break;
      }
      default:
        break;
    }
    return sorted
      .map(i =>
        ProductColorItem.fromPartial({
          items: [
            ProductColorItem_Item.fromPartial({
              listHash: i.hash,
              meta: i.meta,
              colors: i.colors,
            }),
          ],
        })
      )
      .flatMap(i => GridItem.toGridItems(i));
  }

  override getMetaByUrl(segments: string[], model: MiniHome): MetaData | undefined {
    if (segments[0] !== RoutePaths.HOME || !segments[1] || segments[1] === RoutePaths.SALES_EVENT) {
      return undefined;
    }
    const pureName = decodeURIComponent(segments[1]).split('?')[0];
    const image = model.miniHomes.find(m => m.big?.nameEn === pureName)?.image?.hash || model.miniHomes[0]?.image?.hash;
    return new MetaData($localize`${pureName || segments[1]} - 新品`, '', image);
  }

  private secondSort(
    a: MiniHome_MiniHomeProducts_MiniHomeProduct,
    b: MiniHome_MiniHomeProducts_MiniHomeProduct
  ): number {
    if (a.hasInventory === b.hasInventory) {
      return (b.meta?.productId ?? '').localeCompare(a.meta?.productId ?? '');
    }
    return a.hasInventory === b.hasInventory ? 0 : b.hasInventory ? 1 : -1;
  }
}
